import { z } from 'zod';
import { storedHttpUrlSchema } from '../safe-url.js';

/** Address shape matching data_center.address JSONB */
export const orgAddressSchema = z.object({
  street:  z.string().optional(),
  city:    z.string().optional(),
  state:   z.string().optional(),
  postal:  z.string().optional(),
  country: z.string().optional(),
}).nullable().optional();

export type OrgAddress = z.infer<typeof orgAddressSchema>;

/** Contact/identity fields shared by seller_org and buyer_org, minus name and preferences */
export const orgContactFieldsSchema = z.object({
  contactEmail: z.string().email().nullable().optional(),
  contactPhone: z.string().nullable().optional(),
  website:      storedHttpUrlSchema.nullable().optional(),
  description:  z.string().nullable().optional(),
  address:      orgAddressSchema,
  poolNote:     z.string().nullable().optional(),
});
export type OrgContactFields = z.infer<typeof orgContactFieldsSchema>;

/** Fixed enum slugs for logistics preferences (RUF-271) */
export const SHIPPING_METHOD_SLUGS = ['freight', 'palletized', 'white_glove', 'parcel', 'international'] as const;
export const DC_CAPABILITY_SLUGS   = ['standard_density', 'high_density', 'air_cooled', 'liquid_cooled', 'colo', 'on_prem'] as const;

/** Fixed enum slugs for services preferences (RUF-333) — store now, filter later */
export const SERVICE_SLUGS = ['data_wipe', 'asset_tagging', 'install', 'decommission'] as const;

export type ShippingMethodSlug = (typeof SHIPPING_METHOD_SLUGS)[number];
export type DcCapabilitySlug   = (typeof DC_CAPABILITY_SLUGS)[number];
export type ServiceSlug        = (typeof SERVICE_SLUGS)[number];

/**
 * Matching-preference fields shared by seller_org and buyer_org (RUF-270/271/333).
 * prefAssetClasses/prefManufacturers/prefRegions reference live catalog IDs —
 * validated against the DB at the API layer, not by this schema. The
 * logistics/services slugs are fixed enums, validated here and by a DB CHECK.
 */
export const orgPreferenceFieldsSchema = z.object({
  prefAssetClasses:    z.array(z.string()).optional(),
  prefManufacturers:   z.array(z.string()).optional(),
  prefRegions:         z.array(z.string()).optional(),
  prefShippingMethods: z.array(z.enum(SHIPPING_METHOD_SLUGS)).optional(),
  prefDcCapability:    z.array(z.enum(DC_CAPABILITY_SLUGS)).optional(),
  /** RUF-333: value-added services preference (store now, filter later) */
  prefServices:        z.array(z.enum(SERVICE_SLUGS)).optional(),
});
export type OrgPreferenceFields = z.infer<typeof orgPreferenceFieldsSchema>;

/** Profile fields shared by seller_org and buyer_org */
export const orgProfileSchema = z.object({
  legalName:           z.string(),
  shortName:           z.string().nullable(),
  contactEmail:        z.string().nullable(),
  contactPhone:        z.string().nullable(),
  website:             z.string().nullable(),
  description:         z.string().nullable(),
  address:             orgAddressSchema,
  poolNote:            z.string().nullable(),
  prefAssetClasses:    z.array(z.string()).nullable(),
  prefManufacturers:   z.array(z.string()).nullable(),
  prefRegions:         z.array(z.string()).nullable(),
  prefShippingMethods: z.array(z.enum(SHIPPING_METHOD_SLUGS)).nullable(),
  prefDcCapability:    z.array(z.enum(DC_CAPABILITY_SLUGS)).nullable(),
  /** RUF-333: value-added services preference (store now, filter later) */
  prefServices:        z.array(z.enum(SERVICE_SLUGS)).nullable(),
  /**
   * SIL-163/169: direct-offers epic, customer_org-level settings (654).
   * Optional (additive) here — populated once the API surfaces them
   * (SIL-170); stale cached bundles predating that rollout simply omit them.
   */
  acceptUnsolicitedOffers:       z.boolean().optional(),
  /** Minimum premium (percentage points) over MCMP required for auto-eligibility. Bounded [0, 100] (customer_org CHECK, 654). */
  unsolicitedOfferThresholdPct:  z.number().int().min(0).max(100).optional(),
});

export type OrgProfile = z.infer<typeof orgProfileSchema>;

/**
 * RUF-539: per-profile capability summary. `missingFields` passes the shared
 * predicate's machine-readable keys through verbatim — the portal owns label
 * mapping.
 */
export const profileStatusSchema = z.object({
  active:       z.boolean(),
  complete:     z.boolean(),
  missingFields: z.array(z.string()),
});
export type ProfileStatus = z.infer<typeof profileStatusSchema>;

/**
 * GET /api/client/me response shape (RUF-331 dual-profile; RUF-539 additive
 * capability contract).
 *
 * Both sellerProfile and buyerProfile are always present after migration 536.
 *
 * RUF-539 EXPAND phase: canSell/canBuy/profiles are the new additive contract.
 * orgType, sellerProfile, buyerProfile, and orgProfile are retained as
 * deprecated response-level shims — CloudFront-cached portal bundles outlive
 * the Lambda cutover, so removing fields here breaks stale bundles. Removal
 * happens in RUF-542.
 */
export const meResponseSchema = z.object({
  id:                  z.string().uuid(),
  email:               z.string(),
  role:                z.enum(['admin', 'member']),
  /** @deprecated use canSell/canBuy + profiles — legacy single-persona field, removed in RUF-542 */
  orgType:             z.enum(['seller', 'buyer']),
  /** @deprecated use canSell/canBuy + profiles — legacy single-persona field, removed in RUF-542 */
  orgId:               z.string().uuid(),
  orgName:             z.string().nullable(),
  /** RUF-380: org logo (customer portal only; null when unset) */
  logoUrl:             z.string().nullable(),
  /** RUF-380: caller's team (admins have none; drives member scoping UI) */
  teamId:              z.string().uuid().nullable(),
  /**
   * SIL-171: direct-offers seller-side settings (customer_org, 654) —
   * top-level (not nested in orgProfile/sellerProfile/buyerProfile) because
   * they read straight off customer_org, not seller_org/buyer_org.
   */
  acceptUnsolicitedOffers:      z.boolean(),
  unsolicitedOfferThresholdPct: z.number().int().min(0).max(100),
  /**
   * SIL-379: org-level PUE (customer_org.pue, 708). null = not supplied —
   * the industry-default rung applies at compute time. Was missing here
   * since SIL-379 shipped the producer field (SIL-481).
   */
  pue:                 z.number().nullable(),
  pueUpdatedAt:        z.string().nullable(),
  /** RUF-539: true iff the corresponding profile exists, is active, and is complete */
  canSell:             z.boolean(),
  canBuy:              z.boolean(),
  /** RUF-539: per-profile completeness breakdown */
  profiles:            z.object({
    seller: profileStatusSchema,
    buyer:  profileStatusSchema,
  }),
  /**
   * SIL-993: 'business' (default) or 'individual' (861). Drives whether the
   * portal shows the business dual-profile (seller_org/buyer_org) editor or
   * the single individual_profile completion flow. For an individual org,
   * canSell/canBuy/profiles.* above are derived from individual_profile
   * completeness (unlock together), NOT from seller_org/buyer_org fields —
   * those rows stay marketplace anchors only.
   *
   * SIL-1040: nullable — null means the customer_org row backing this user
   * couldn't be resolved to a known org type (clientAuth.ts). Previously the
   * producer (me.ts) coerced this to 'business' for display, which made the
   * portal render business-only pages that then 403 at the API gate. The
   * portal treats null as PENDING (useOrgFeatureState/orgFeatureStateOf
   * already do — organizationType == null is the same branch as
   * isLoading), never as 'business'.
   */
  organizationType:    z.enum(['business', 'individual']).nullable(),
  /**
   * SIL-993: individual_profile missing-field keys (machine-readable, see
   * individualProfileCompleteness in dto/individual-profile.ts). Always []
   * for a business org. canSell === canBuy === (this array is empty) for an
   * individual org — the single unlock-together gate.
   */
  individualProfileMissing: z.array(z.string()),
  /**
   * SIL-1007: true iff a tos_acceptance row exists for this user at the
   * CURRENT TOS_VERSION (dto/tos.ts). Sourced from c.var.clientUser
   * (clientAuthMiddleware already resolved it enforcing the hard block),
   * never re-queried here. A TOS_VERSION bump makes every existing user's
   * most recent acceptance stale automatically — this flips to false
   * platform-wide on next request with no backfill.
   */
  tosAccepted: z.boolean(),
  /**
   * SIL-994: the portal's single org-identity label — org.legalName for a
   * business, "First Last" (individual_profile) for an individual. null only
   * when neither source has a value yet (e.g. an individual who hasn't
   * entered a name). Consume this instead of orgName for anything showing
   * "who is this" (portal shell header/title, document.title) — orgName
   * stays legalName-only (null for an individual, per 861) and is retained
   * as a deprecated shim.
   */
  displayName:         z.string().nullable(),
  /** @deprecated use canSell/canBuy + profiles — dual-profile (RUF-331) shim, removed in RUF-542 */
  sellerProfile:       orgProfileSchema.nullable(),
  /** @deprecated use canSell/canBuy + profiles — dual-profile (RUF-331) shim, removed in RUF-542 */
  buyerProfile:        orgProfileSchema.nullable(),
  /** @deprecated use canSell/canBuy + profiles — removed in RUF-542 */
  orgProfile:          orgProfileSchema.nullable(),
  onboardingCompleted: z.boolean().optional(),
  invitedAt:           z.string().nullable(),
  lastLoginAt:         z.string().nullable(),
  /**
   * SIL-1132: true iff the org owns at least one asset (`asset.customer_org_id`).
   * Drives the individual-org first-login routing (__root.tsx's
   * IndividualProfileGate/AddAssetGate): a fresh individual org with no
   * assets is routed to the Add Asset wizard instead of profile completion;
   * profile completion is deferred until either the sell flow is attempted
   * (component-level canSell gates) or the org's first asset exists.
   */
  hasAssets: z.boolean(),
  /**
   * 929: own-org nudge counts for one-time manual serial entry (individual
   * orgs, TrueRead-probe assets that still have no serial). Optional so an
   * older API deploy (field absent) reads as 0. No serials, no foreign ids.
   */
  serialEntryPendingCount: z.number().int().nonnegative().optional(),
  /** Distinct servers among the pending assets (child GPUs roll up to their server). */
  serialEntryServerCount: z.number().int().nonnegative().optional(),
  /** Oldest pending asset of THIS org - where the nudges link to. */
  serialEntryFirstAssetId: z.string().uuid().nullable().optional(),
});

export type MeResponse = z.infer<typeof meResponseSchema>;

/**
 * RUF-534: shared "is this profile complete enough to transact" predicate.
 * EXPAND phase of an expand-contract refactor — lands alongside the legacy
 * orgType/sellerOrgId/buyerOrgId fields, which are untouched until RUF-542.
 *
 * Column list every call site (client auth, /me, ops customer detail) must
 * select so they all evaluate the predicate against identical inputs —
 * prevents "UI says complete, gate says 403" drift.
 */
export const PROFILE_COMPLETENESS_COLUMNS = ['legalName', 'contactEmail', 'contactPhone', 'address'] as const;

export type ProfileCompletenessRow = {
  legalName: string | null | undefined;
  contactEmail: string | null | undefined;
  contactPhone: string | null | undefined;
  address: unknown;
};

export type ProfileCompletenessMissingField = 'legalName' | 'contactEmail' | 'contactPhone' | 'address';

export interface ProfileCompletenessResult {
  complete: boolean;
  missing: ProfileCompletenessMissingField[];
}

/** True for a string with non-whitespace content; false for null/undefined/blank. */
function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Complete = trimmed non-empty legalName, contactEmail, contactPhone, and an
 * address jsonb with non-empty street, city, country (state/postal optional).
 *
 * Handles address edge cases: null, undefined, {}, non-object values, and
 * empty/whitespace-only string fields — all treated as an incomplete address.
 */
export function profileCompleteness(row: ProfileCompletenessRow): ProfileCompletenessResult {
  const missing: ProfileCompletenessMissingField[] = [];

  if (!isNonEmptyString(row.legalName)) missing.push('legalName');
  if (!isNonEmptyString(row.contactEmail)) missing.push('contactEmail');
  if (!isNonEmptyString(row.contactPhone)) missing.push('contactPhone');

  const address = row.address;
  const isAddressObject = typeof address === 'object' && address !== null && !Array.isArray(address);
  const addressRecord = isAddressObject ? (address as Record<string, unknown>) : {};
  const hasAddressEssentials =
    isNonEmptyString(addressRecord['street']) &&
    isNonEmptyString(addressRecord['city']) &&
    isNonEmptyString(addressRecord['country']);
  if (!hasAddressEssentials) missing.push('address');

  return { complete: missing.length === 0, missing };
}

/**
 * SIL-2169: GET /api/client/me/escrow-status. Per side, does the caller's org have a live Escrow.com
 * transaction (migration 1167, get_my_org_open_escrow_transaction)? While true, that side's contact email
 * is locked: Escrow.com matches the party by it, and the database refuses a change (EC008, HTTP 409
 * `escrow_email_locked`). Booleans only.
 */
export const escrowOrgStatusSchema = z.object({
  buyer:  z.boolean(),
  seller: z.boolean(),
});
export type EscrowOrgStatus = z.infer<typeof escrowOrgStatusSchema>;
